import React from 'react';
import PropTypes from 'prop-types';
import LazyLoad from 'react-lazyload';
import Img from 'react-image';

/**
 * Allow ui-common consumers to force lazy-loaded images to check whether they
 * are in the viewport
 */
export { forceCheck as lazyLoadForceCheck } from 'react-lazyload';

/**
 * Lazy Loaded Image
 * react-lazyload renders any component once it's near the viewport (offset)
 * react-image is a helper for loading an image with a small placeholder to prevent page jumps
 * https://css-tricks.com/content-jumping-avoid/
 *
 * The `placholder` node should be around the same size as the `src` image. A low quality image
 * of the same dimensions works good for this, or a div of the same dimensions.
 */
// eslint-disable-next-line react/prefer-stateless-function
class LazyImage extends React.PureComponent {
  render() {
    const {
      className,
      src,
      alt,
      lazy,
      placeholder,
      lazyLoadProps,
      imageProps
    } = this.props;

    // Turn off decoding feature in react-image for SVGs to avoid this bug in
    // safari: https://bugs.webkit.org/show_bug.cgi?id=188347
    const isSvg = /.svg/.test(src);
    const image = (
      <Img
        className={className}
        src={src}
        loader={placeholder}
        alt={alt}
        decode={!isSvg}
        {...imageProps} />
    );

    if (!lazy) {
      return image;
    }

    return (
      <LazyLoad {...lazyLoadProps} placeholder={placeholder}>
        {image}
      </LazyLoad>
    );
  }
}

// See react-lazyload README for info on LazyLoad props: https://github.com/twobin/react-lazyload#props
export const lazyLoadPropType = {
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  once: PropTypes.bool,
  offset: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
  scroll: PropTypes.bool,
  resize: PropTypes.bool,
  overflow: PropTypes.bool,
  placeholder: PropTypes.node,
  unmountIfInvisible: PropTypes.bool,
  debounce: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  throttle: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
};

export const lazyLoadDefaultProp = {
  height: undefined,
  once: true,
  offset: 100,
  scroll: true,
  resize: false,
  overflow: false,
  placeholder: null,
  unmountIfInvisible: false,
  debounce: undefined,
  throttle: 50,
};

LazyImage.propTypes = {
  className: PropTypes.string,
  lazy: PropTypes.bool,
  src: PropTypes.string,
  // lazy load props
  ...lazyLoadPropType,
};

LazyImage.defaultProps = {
  className: '',
  lazy: true,
  src: '',
  // lazy load props
  ...lazyLoadDefaultProp,
};

export default LazyImage;
